import { useRef, useEffect, useMemo } from "react";
import ChatBubble from "./ChatBubble";
import ToolExecution from "./ToolExecution";
import type { AiError } from "./useAiProgress";

export interface AiProgressUpdate {
  id: number | string;
  timestamp?: Date;
  aiMessage?: string;
  toolExecutionStarted?: string;
  toolExecutionEnd?: string;
  toolExecutionFailed?: boolean;
  toolExecutionId?: string;
  toolArguments?: string;
  toolResult?: string;
  chatUserMessage?: string;
  chatStreamChunk?: string;
  chatError?: AiError;
}

interface AiProgressUpdatesProps {
  updates: AiProgressUpdate[];
  activeTools: Set<string>;
  autoScroll?: boolean;
}

/**
 * Renders AI progress updates including:
 * - AI intermediate messages
 * - Tool execution start (with glow effect when active)
 * - Tool execution end
 */
export const AiProgressUpdates = ({
  updates,
  activeTools,
  autoScroll = true,
}: AiProgressUpdatesProps) => {
  const progressEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new updates are added
  useEffect(() => {
    if (autoScroll && updates.length > 0 && progressEndRef.current) {
      progressEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [updates, autoScroll]);

  // Build a map of toolExecutionId -> end event data for correlation
  const toolEndDataByExecutionId = useMemo(() => {
    const map = new Map<string, { toolArguments?: string; toolResult?: string }>();
    for (const update of updates) {
      if (update.toolExecutionEnd && update.toolExecutionId) {
        map.set(update.toolExecutionId, {
          toolArguments: update.toolArguments,
          toolResult: update.toolResult,
        });
      }
    }
    return map;
  }, [updates]);

  if (updates.length === 0) {
    return null;
  }

  // Determine if update needs wrapper margin - tool executions handle their own spacing
  const needsWrapperMargin = (update: AiProgressUpdate) => {
    // Tool executions handle their own margins via ToolExecution component
    if (update.toolExecutionStarted || update.toolExecutionEnd) {
      return false;
    }
    return true;
  };

  // Get correlated tool result for a started event
  const getCorrelatedResult = (update: AiProgressUpdate) => {
    if (update.toolExecutionId) {
      return toolEndDataByExecutionId.get(update.toolExecutionId)?.toolResult;
    }
    return undefined;
  };

  return (
    <div className="ai-progress-updates mb-3">
      {updates.map((update) => (
        <div key={update.id} className={needsWrapperMargin(update) ? "mb-2" : ""}>
          {update.chatUserMessage && (
            <ChatBubble type="user">{update.chatUserMessage}</ChatBubble>
          )}
          {update.aiMessage && (
            <ChatBubble type="ai" markdown={update.aiMessage} />
          )}
          {update.toolExecutionStarted && (
            <ToolExecution
              toolName={update.toolExecutionStarted}
              status="started"
              isActive={activeTools.has(update.toolExecutionStarted)}
              toolArguments={update.toolArguments}
              toolResult={getCorrelatedResult(update)}
            />
          )}
          {update.toolExecutionEnd && (
            <ToolExecution
              toolName={update.toolExecutionEnd}
              status={update.toolExecutionFailed ? "failed" : "completed"}
              toolArguments={update.toolArguments}
              toolResult={update.toolResult}
            />
          )}
          {update.chatStreamChunk && (
            <ChatBubble type="ai" markdown={update.chatStreamChunk} />
          )}
          {update.chatError && (
            <ChatBubble type="system">{update.chatError.message}</ChatBubble>
          )}
        </div>
      ))}
      <div ref={progressEndRef} />
    </div>
  );
};

export default AiProgressUpdates;
